import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Brain, TrendingUp, AlertTriangle, Wrench, Zap, CheckCircle, Clock, X } from 'lucide-react';
import { AIInsight } from '@/types/ai';
import { aiService } from '@/services/ai-service';
import { useGetDevicesQuery } from '@/services';

const iconMap = {
  trend: TrendingUp,
  anomaly: AlertTriangle,
  optimization: Zap,
  prediction: Clock,
  maintenance: Wrench,
};

const severityColors = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

interface AIInsightsPanelProps {
  className?: string;
}

export function AIInsightsPanel({ className }: AIInsightsPanelProps) {
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissedInsights, setDismissedInsights] = useState<Set<string>>(new Set());

  const { data: devicesData } = useGetDevicesQuery({ includeSensors: 'true' });

  useEffect(() => {
    const generateInsights = async () => {
      if (!devicesData?.data) return;

      setLoading(true);
      try {
        const generatedInsights = await aiService.generateInsights(devicesData.data, []);
        setInsights(generatedInsights);
      } catch (error) {
        console.error('Failed to generate AI insights:', error);
      } finally {
        setLoading(false);
      }
    };

    generateInsights();

    // Refresh insights every 30 seconds
    const interval = setInterval(generateInsights, 30000);
    return () => clearInterval(interval);
  }, [devicesData]);

  const handleDismissInsight = (insightId: string) => {
    setDismissedInsights((prev) => new Set([...prev, insightId]));
  };

  const visibleInsights = insights.filter((insight) => !dismissedInsights.has(insight.id));

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-blue-500" />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Analyzing your IoT data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-blue-500" />
          AI Insights
          {visibleInsights.length > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {visibleInsights.length} new
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {visibleInsights.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No new insights available</p>
            <p className="text-sm">AI is monitoring your devices</p>
          </div>
        ) : (
          visibleInsights.map((insight) => {
            const Icon = iconMap[insight.type];
            return (
              <div
                key={insight.id}
                className="border rounded-lg p-4 space-y-3 relative group hover:shadow-sm transition-shadow"
              >
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDismissInsight(insight.id)}
                >
                  <X className="h-3 w-3" />
                </Button>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <Icon className="h-5 w-5 text-blue-500 mt-0.5" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <Badge className={`text-xs ${severityColors[insight.severity]}`}>{insight.severity}</Badge>
                      <Badge variant="outline" className="text-xs">
                        {insight.confidence}% confident
                      </Badge>
                    </div>

                    <p className="text-sm text-muted-foreground">{insight.description}</p>

                    {insight.suggestedActions && insight.suggestedActions.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Suggested Actions:</p>
                        <ul className="text-xs space-y-1">
                          {insight.suggestedActions.slice(0, 2).map((action, index) => (
                            <li key={index} className="flex items-center gap-1 text-muted-foreground">
                              <div className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <div className="text-xs text-muted-foreground">
                        {insight.affectedDevices.length} device(s) affected
                      </div>
                      <div className="text-xs text-muted-foreground">{insight.timestamp.toLocaleTimeString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
